import React, { useEffect, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import s from "./CompareItemsModal.module.css";

/**
 * You can extend SPEC_ROWS any time; compare stays dynamic.
 * Keys are read via `read()` with multiple aliases.
 */
const SPEC_ROWS = [
  { key: "usage",     label: "Usage" },
  { key: "brand",     label: "Brand" },
  {
    key: "dimmable",
    label: "Dimmable",
    format: (v) =>
      v === true || String(v).toLowerCase() === "yes"
        ? "Yes"
        : v === false || String(v).toLowerCase() === "no"
        ? "No"
        : v ?? "—",
  },
  { key: "colorTemp", label: "Color Temp" },
  { key: "power",     label: "Power" },
  { key: "size",      label: "Size" },
  {
    key: "price",
    label: "Price",
    format: (v) => (typeof v === "number" ? `$${v}` : v ?? "—"),
  },
];

/** Safe getter with common alt keys */
const read = (p, k) => {
  if (!p) return "—";
  if (p[k] != null) return p[k];
  const alts = {
    usage: ["usecase", "application", "category", "subtitle"],
    brand: ["brandName", "manufacturer"],
    dimmable: ["isDimmable", "dimmable"],
    colorTemp: ["color_temperature", "cct", "temperature"],
    power: ["watt", "wattage"],
    size: ["dimensions"],
    price: ["msrp"],
  }[k] || [];
  for (const a of alts) if (p[a] != null) return p[a];
  return "—";
};

export default function CompareItemsModal({ open, onClose, products = [] }) {
  // Accept up to 4 columns gracefully
  const cols = useMemo(() => products.slice(0, 4), [products]);

  // ESC to close
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const formatVal = (row, raw) => (row.format ? row.format(raw) : (raw ?? "—"));

  const handleExportPDF = useCallback(() => {
    const orientation = cols.length > 2 ? "landscape" : "portrait";
    const doc = new jsPDF({ orientation, unit: "pt", format: "a4" });
    const margin = 36;

    const title = "Compare items";
    const dateStr = new Date().toLocaleString();

    // Header
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.text(title, margin, 28);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.text(dateStr, margin, 42);

    // Build table head (two rows: names + subtitles/brands)
    const headRow1 = ["Specifications", ...cols.map((p) => p?.name || "—")];
    const headRow2 = ["", ...cols.map((p) => p?.subtitle || p?.brand || "—")];

    // Body
    const body = SPEC_ROWS.map((row) => {
      const values = cols.map((p) => {
        const raw = read(p, row.key);
        const v = formatVal(row, raw);
        return v == null ? "—" : String(v);
      });
      return [row.label, ...values];
    });

    autoTable(doc, {
      head: [headRow1, headRow2],
      body,
      startY: 56,
      theme: "grid",
      styles: { fontSize: 9, cellPadding: 6, overflow: "linebreak" },
      headStyles: {
        fillColor: [240, 240, 240],
        textColor: [0, 0, 0],
        fontStyle: "bold",
      },
      columnStyles: {
        0: { cellWidth: 160, fontStyle: "bold" }, // "Specifications" column wider
      },
      margin: { left: margin, right: margin },
      didDrawPage: () => {
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        doc.setFontSize(8);
        doc.setTextColor(120);
        doc.text(
          `Page ${doc.internal.getNumberOfPages()}`,
          pageWidth - margin,
          pageHeight - 16,
          { align: "right" }
        );
      },
    });

    doc.setProperties({ title: `${title} (${dateStr})` });
    doc.save(`Compare_${new Date().toISOString().slice(0, 10)}.pdf`);
  }, [cols]);

  if (!open) return null;

  return createPortal(
    <div className={s.portalRoot} aria-modal="true" role="dialog" aria-label="Compare items">
      {/* backdrop */}
      <div className={s.backdrop} onClick={onClose} />

      {/* modal */}
      <div className={s.card} role="document">
        <div className={s.header}>
          <div className={s.headerTitle}>Compare items</div>

          <div className={s.headerActions}>
            <button
              className={s.exportBtn}
              aria-label="Export PDF"
              onClick={handleExportPDF}
              disabled={!cols.length}
              title="Export PDF"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M12 3v10m0 0l-4-4m4 4l4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M20 14v3a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <span>Export PDF</span>
            </button>

            <button className={s.closeBtn} aria-label="Close" onClick={onClose}>
              <svg width="14" height="14" viewBox="0 0 20 20" fill="none">
                <path d="M5 5L15 15M15 5L5 15" stroke="white" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
          </div>
        </div>

        {/* Scroll area (Y + X) */}
        <div className={s.scrollWrap}>
          <table className={s.table}>
            <thead className={s.thead}>
              <tr>
                <th className={`${s.th} ${s.thSpecs}`}>Specifications</th>
                {cols.map((p) => (
                  <th key={p.id || Math.random()} className={`${s.th} ${s.thProduct}`}>
                    <div className={s.prodTitle} title={p.name}>{p.name}</div>
                    <div className={s.prodSub}>{p.subtitle || p.brand || "—"}</div>
                  </th>
                ))}
              </tr>
            </thead>

            <tbody className={s.tbody}>
              {SPEC_ROWS.map((row) => (
                <tr key={row.key} className={s.tr}>
                  <td className={`${s.td} ${s.specCell}`}>{row.label}</td>
                  {cols.map((p) => {
                    const raw = read(p, row.key);
                    const val = formatVal(row, raw);
                    return (
                      <td key={`${row.key}-${p.id || Math.random()}`} className={s.td}>
                        {val ?? "—"}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>,
    document.body
  );
}
